import { formatDate, NgIf, NgFor } from '@angular/common';
import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { ApiError, GenericApiResponse } from 'src/app/interfaces/api';
import ReportsService, {
  PremiumPartner,
} from 'src/app/services/reports.service';
import { DatePickerComponent } from '../date-picker/date-picker.component';

@Component({
    selector: 'app-generate-reports',
    templateUrl: './generate-reports.component.html',
    styleUrls: ['./generate-reports.component.scss'],
    imports: [
        FormsModule,
        ReactiveFormsModule,
        NgIf,
        NgFor,
        DatePickerComponent,
    ],
})
export class GenerateReportsComponent implements OnInit {
  public reportForm!: UntypedFormGroup;

  public reportParams!: ReportParams;

  public submitted = false;

  public loading = false;

  public fromDateError = false;

  public toDateError = false;

  public datesInvalid = false;

  public reportSuccess = false;

  public reportFailed = false;

  public missingExportName = false;

  public reportErrorMessage = 'Failed to generate report';

  public premiumPartners: PremiumPartner[] = [];

  private cdr = inject(ChangeDetectorRef);

  public reportColumnsArray: Checkboxes[] = [
    { id: 'id', label: 'Id', selected: true },
    { id: 'uuid', label: 'UUID', selected: true },
    {
      id: 'first_name',
      label: 'First Name',
      selected: true,
    },
    { id: 'last_name', label: 'Last Name', selected: true },
    { id: 'dob', label: 'Date of Birth', selected: true },
    { id: 'email', label: 'Email', selected: true },
    { id: 'university_id', label: 'University Id', selected: true },
    { id: 'university_name', label: 'University Name', selected: true },
    { id: 'accommodation_id', label: 'Accomodation Id', selected: true },
    { id: 'accomodation_name', label: 'Accomodation Name', selected: true },
    { id: 'city_id', label: 'City Id', selected: true },
    { id: 'confirmed_cover', label: 'Confirmed Cover', selected: true },
    { id: 'accepted_email', label: 'Accepted Email', selected: true },
    {
      id: 'accepted_notifications',
      label: 'Accepted Notifications',
      selected: true,
    },
    { id: 'email_verified_at', label: 'Email Verified at', selected: true },
    { id: 'cover_email_sent_at', label: 'Cover email sent at', selected: true },
    {
      id: 'registration_platform',
      label: 'Registration Platform',
      selected: true,
    },
    { id: 'accepted_tracking', label: 'Accepted Tracking', selected: true },
    { id: 'registration_method', label: 'Registration Method', selected: true },
    { id: 'product_names', label: 'Product Names', selected: true },
    { id: 'created_at', label: 'Created at', selected: true },
    { id: 'updated_at', label: 'Updated at', selected: true },
    { id: 'imported_at', label: 'Imported at', selected: true },
    { id: 'source', label: 'Login Source', selected: true },
    { id: 'type', label: 'Login Type', selected: true },
    { id: 'login_attempt_at', label: 'Last Login Attempt at', selected: true },
    { id: 'postcode', label: 'Postcode', selected: true },
  ];

  public universityReportColumnsArray: Checkboxes[] = [
    { id: 'updated_at', label: 'Updated at', selected: true },
    { id: 'uuid', label: 'UUID', selected: true },
    {
      id: 'first_name',
      label: 'First Name',
      selected: true,
    },
    { id: 'last_name', label: 'Last Name', selected: true },
    { id: 'dob', label: 'Date of Birth', selected: true },
    { id: 'email', label: 'Email', selected: true },
    { id: 'from_university_id', label: 'From University Id', selected: true },
    {
      id: 'from_university_name',
      label: 'From University Name',
      selected: true,
    },
    { id: 'to_university_id', label: 'To University Id', selected: true },
    { id: 'to_university_name', label: 'To University Name', selected: true },
    {
      id: 'from_accommodation_id',
      label: 'From Accommodation Id',
      selected: true,
    },
    {
      id: 'from_accommodation_name',
      label: 'From Accommodation Name',
      selected: true,
    },
    { id: 'to_accommodation_id', label: 'To Accommodation Id', selected: true },
    {
      id: 'to_accommodation_name',
      label: 'To Accommodation Name',
      selected: true,
    },
    { id: 'postcode', label: 'Postcode', selected: true },
  ];

  public dateFilters = [
    { id: 'created_at', label: 'Registrations between' },
    { id: 'updated_at', label: 'Last updated between' },
    { id: 'login_attempt_at', label: 'Last login attempt between' },
    {
      id: 'university_updated_at',
      label: 'University/Accomodation updated between',
    },
  ];

  constructor(
    public readonly formBuilder: UntypedFormBuilder,
    private readonly reportsService: ReportsService
  ) {}

  ngOnInit() {
    this.reportForm = this.formBuilder.group({
      exportName: new UntypedFormControl('', [Validators.required.bind(this)]),
      reportColumns: new UntypedFormArray([]),
      universityReportColumns: new UntypedFormArray([]),
      dateFilter: new UntypedFormControl('created_at'),
      premiumPartner: new UntypedFormControl(''),
      fromDate: new UntypedFormControl(null),
      toDate: new UntypedFormControl(null),
    });

    this.getPremiumPartners();

    this.addCheckboxes();
    this.onChanges();
  }

  onChanges() {
    this.reportForm.get('dateFilter')?.valueChanges.subscribe((filter) => {
      if (filter) {
        this.reportForm
          .get('toDate')
          ?.addValidators([Validators.required.bind(this)]);
        this.reportForm
          .get('fromDate')
          ?.addValidators([Validators.required.bind(this)]);
      } else {
        this.reportForm.get('toDate')?.clearValidators();
        this.reportForm.get('fromDate')?.clearValidators();
        this.fromDateError = false;
        this.toDateError = false;
      }
      this.reportForm.get('toDate')?.updateValueAndValidity();
      this.reportForm.get('fromDate')?.updateValueAndValidity();
    });

    this.reportForm.get('toDate')?.valueChanges.subscribe((toDate) => {
      const fromDate = this.reportForm.get('fromDate')?.value as Date;
      this.datesInvalid = fromDate && toDate < fromDate;
      this.toDateError = false;
    });

    this.reportForm.get('fromDate')?.valueChanges.subscribe((fromDate) => {
      const toDate = this.reportForm.get('toDate')?.value as Date;
      this.datesInvalid = toDate && toDate < fromDate;
      this.fromDateError = false;
    });

    this.reportForm.get('exportName')?.valueChanges.subscribe((name) => {
      if (name) {
        this.missingExportName = false;
      } else {
        this.missingExportName = this.submitted;
      }
    });
  }

  public get reportColumnValues(): UntypedFormArray {
    return this.reportForm.controls['reportColumns'] as UntypedFormArray;
  }

  public get universityReportColumnValues(): UntypedFormArray {
    return this.reportForm.controls['universityReportColumns'] as UntypedFormArray;
  }

  public selectOrDeselect(value: boolean) {
    const reportColumns = this.universityUpdateReport
      ? this.universityReportColumnValues
      : this.reportColumnValues;

    reportColumns.controls.forEach((control) => control.setValue(value));
  }

  public generateReport() {
    this.submitted = true;
    this.loading = true;
    this.reportFailed = false;
    this.reportSuccess = false;
    const toDate = this.reportForm.get('toDate');
    const fromDate = this.reportForm.get('fromDate');
    const dateFilter = !!this.reportForm.get('dateFilter')?.value;

    this.fromDateError = (dateFilter && !fromDate?.valid) || false;

    this.toDateError = (dateFilter && !toDate?.valid) || false;

    this.missingExportName = !this.reportForm.get('exportName')?.value;
    if (this.missingExportName) {
      document.getElementById('scroll-export-name')?.scrollIntoView();
    }

    if (!this.reportForm.valid || this.datesInvalid) {
      this.loading = false;
      return;
    }

    const reportColumns: string[] = [];

    if (this.universityUpdateReport) {
      this.universityReportColumnValues.controls.forEach((col, index) => {
        if (col.value) {
          reportColumns.push(this.universityReportColumnsArray[index].id);
        }
      });
    } else {
      this.reportColumnValues.controls.forEach((col, index) => {
        if (col.value) {
          reportColumns.push(this.reportColumnsArray[index].id);
        }
      });
    }

    this.reportParams = {
      exportName: this.reportForm.get('exportName')?.value as string,
      reportColumns,
      dateFilterType: this.reportForm.get('dateFilter')?.value as string,
      premiumPartner: this.reportForm.get('premiumPartner')!.value,
      // Use calendar year to avoid NG02300 week-year warning
      fromDate: formatDate(fromDate?.value as Date, 'yyyyMMdd', 'en-US'),
      toDate: formatDate(toDate?.value as Date, 'yyyyMMdd', 'en-US'),
    };

    const reportMethod = this.universityUpdateReport
      ? this.reportsService.generateProviderMovementReport(this.reportParams)
      : this.reportsService.generateReport(this.reportParams);

    reportMethod
      .pipe(
        finalize(() => {
          this.loading = false;
          setTimeout(() => {
            this.reportSuccess = false;
            this.reportFailed = false;
          }, 5000);
        })
      )
      .subscribe({
        next: () => {
          this.reportSuccess = true;
          this.cdr.markForCheck()
        },
        error: (error: ApiError) => {
          if (error.error?.message === 'name exists') {
            this.reportErrorMessage =
              'A file with this name has already been created. Please retry using an alternative name.';
          }
          this.reportFailed = true;
          this.cdr.markForCheck()
        },
      });
  }

  private getPremiumPartners(): void {
    this.reportsService.getPremiumPartners().subscribe({
      next: (res: GenericApiResponse<PremiumPartner[]>) => {
        this.premiumPartners = res.data;
      },
    });
  }

  private addCheckboxes() {
    this.reportColumnsArray.forEach((value) =>
      this.reportColumnValues.push(new UntypedFormControl(value.selected))
    );

    this.universityReportColumnsArray.forEach((value) =>
      this.universityReportColumnValues.push(new UntypedFormControl(value.selected))
    );
  }

  public get universityUpdateReport(): boolean {
    return this.reportForm.get('dateFilter')!.value === 'university_updated_at';
  }
}

export type Checkboxes = {
  id: string;
  label: string;
  selected: boolean;
};

export type ReportParams = {
  exportName: string;
  reportColumns: string[];
  dateFilterType: string;
  premiumPartner: string;
  fromDate: string;
  toDate: string;
};
